/* eslint-disable react/jsx-props-no-spreading */
import type { Icon } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import type React from 'react';
import type { HTMLAttributeAnchorTarget } from 'react';
import { forwardRef } from 'react';
import type { LinkButtonVariant } from './link-button.types';

type IconPosition = 'left' | 'right';

export type LinkButtonProps = {
  'data-testid'?: string;
  ariaLabel?: string;
  children: string;
  className?: string;
  href?: string;
  iconComponent?: Icon;
  iconPosition?: IconPosition;
  onClick?: (
    event:
      | React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
      | React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>
      | MouseEvent,
  ) => void;
  target?: HTMLAttributeAnchorTarget;
  variant?: LinkButtonVariant;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
  destructive?: boolean;
};

const smallSizes: LinkButtonVariant[] = ['snippet-2', 'snippet-3'];

const LinkButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkButtonProps>(
  (
    {
      ariaLabel,
      children,
      className,
      disabled = false,
      destructive,
      form,
      href,
      iconComponent: Icon,
      iconPosition = 'right',
      onClick,
      target,
      type = 'button',
      variant = 'label-2',
      ...props
    }: LinkButtonProps,
    ref,
  ) => {
    const renderedIcon = Icon && <Icon />;
    const sharedProps = {
      ...props,
      'aria-label': ariaLabel,
      className: cx(
        'fp-link-button fp-custom-appearance',
        destructive && 'fp-link-button-destructive',
        `text-${variant}`,
        'inline-flex items-center bg-transparent border-none no-underline w-fit whitespace-nowrap align-middle p-0 m-0',
        smallSizes.includes(variant) ? 'gap-0.5' : 'gap-1',
        disabled ? 'cursor-default' : 'cursor-pointer',
        className,
      ),
      'data-icon-position': iconPosition,
      'data-variant': variant,
      disabled,
      onClick: disabled ? undefined : onClick,
      role: href ? 'link' : 'button',
    };

    if (href) {
      return (
        <a
          {...sharedProps}
          href={disabled ? undefined : href}
          ref={ref as React.Ref<HTMLAnchorElement>}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          target={target}
        >
          {iconPosition === 'left' && renderedIcon}
          <span className="flex items-center h-full">{children}</span>
          {iconPosition === 'right' && renderedIcon}
        </a>
      );
    }

    return (
      <button {...sharedProps} form={form} ref={ref as React.Ref<HTMLButtonElement>} type={type}>
        {iconPosition === 'left' && renderedIcon}
        <span className="flex items-center h-full">{children}</span>
        {iconPosition === 'right' && renderedIcon}
      </button>
    );
  },
);

export default LinkButton;
